import { formatDate } from '@angular/common';
import { IBlockTimeItem } from 'src/app/ReusableComponent/block-time-report/block-time-report.model';
import { IbRepoVerificationReasonEnum } from 'src/app/ReusableComponent/block-time-report/enums/ib-repo-verification-reason.enum';
import { IbRepoVerificationStateInterface } from '../interfaces/ib-repo-verification-state.interface';
import { BLOCK_TIME_EMPTY_VALUE } from '../properties/block-time-record-modal.properties';

/**
 * Maps a block time record to the view model of the IB repo verification.
 */
export function mapIbRepoVerificationState(record: Partial<IBlockTimeItem>): IbRepoVerificationStateInterface {
  const isVerified = Boolean(record.ibRepoVerified);
  const isAwaiting = !isVerified && Boolean(record.ibRepoAwaitingVerification);

  return {
    status: isVerified ? 'verified' : isAwaiting ? 'awaiting' : 'none',
    badgeLabel: isVerified ? 'Verified' : 'Needs verification',
    badgeTheme: isVerified ? 'success' : 'warning',
    badgeIcon: isVerified ? 'check' : 'warning',
    actionText: isVerified ? 'Change hours' : 'Verify hours',
    verifiedText: isVerified ? buildVerifiedText(record.ibRepoVerifiedByName, record.ibRepoVerifiedOn) : null,
    description: buildDescription(record),
    initialHours: record.ibRepoVerifiedHours ?? record.ibRepoCapturedHours ?? record.ibRepoExpectedHours ?? null,
    note: record.ibRepoVerificationNote ?? null,
  };
}

/**
 * Builds the panel text from the reason, the planned airport and the landed airport.
 */
function buildDescription(record: Partial<IBlockTimeItem>): string {
  const plannedAirport = record.inboundReposition || BLOCK_TIME_EMPTY_VALUE;
  const landedAirport = record.actualInBoundReposition || null;
  const checkText = 'Check the hours against FlightAware and correct them if needed.';

  switch (record.ibRepoVerificationReason) {
    case IbRepoVerificationReasonEnum.AirportMismatch:
      return `The aircraft was planned to return to ${plannedAirport} but landed at ${landedAirport ?? BLOCK_TIME_EMPTY_VALUE}. ${checkText}`;
    case IbRepoVerificationReasonEnum.NoDeparture:
      return `Tracking did not capture the inbound reposition to ${plannedAirport}. Check the hours against FlightAware and enter them.`;
    case IbRepoVerificationReasonEnum.IncompleteCapture:
      return `The aircraft landed at ${plannedAirport}, but tracking did not capture the departure time. Check the hours against FlightAware and enter them.`;
    default:
      return `The aircraft was planned to return to ${plannedAirport}. ${checkText}`;
  }
}

function buildVerifiedText(name: string | null | undefined, verifiedOn: string | null | undefined): string {
  const byText = name ? ` by ${name}` : '';
  const onText = verifiedOn ? ` on ${formatDate(verifiedOn, 'MM/dd/yyyy', 'en-US')}` : '';

  return `Verified${byText}${onText}`;
}
